<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		Modal,
		Container,
		Text,
		Alert,
		Checkbox,
		WidgetCard,
		Button,
		Label,
		LabelList,
		DeviceButton,
	} from '@Shelly/shelly-design-system';
	import { requestChangeDashboardSettings } from '../list-helpers';
	import { defaultDashboardSettings } from '../../../helpers/constants';
	import PreviewBox from 'src/components/PreviewBox.svelte';

	export let dashboardId;
	export let showCustomizeCardModal = false;
	export let dashboardSettings = defaultDashboardSettings;

	// Rename type haveHeading to Title.
	// Users will see only one option, but the WidgetCard component will still accept both heading and subheading separately.
	const dashCustomizeCheckboxes = [
		{
			text: $format('label_custom_dash_picture', {
				default: 'Picture',
			}),
			type: 'havePicture',
		},
		{
			text: $format('label_custom_dash_heading', {
				default: 'Title',
			}),
			type: 'haveHeading',
		},
		// {
		// 	text: $format('label_custom_dash_subheading', {
		// 		default: 'Subheading',
		// 	}),
		// 	type: 'haveSubheading',
		// },
		{
			text: $format('label_custom_dash_small_params', {
				default: 'Small Parameters',
			}),
			type: 'haveSmallParams',
		},
		{
			text: $format('label_custom_dash_big_params', {
				default: 'Big Parameter / Action',
			}),
			type: 'haveBigParams',
		},
	];

	const previwCardLabelList = Array.from(Array(10).keys()).map((_, i) => {
		return {
			icon: i % 2 ? 'fas fa-temperature-three-quarters' : 'fas fa-bolt',
			id: i,
			value: i % 2 ? '17.2° C' : 18,
		};
	});

	$: customCardDashSettings = { ...dashboardSettings };

	$: numberOfActiveCardSettings = Object.entries(customCardDashSettings).filter(
		([key, value]) => value === true && key !== 'haveSubheading',
	).length;

	const onCloseCustomizeCardModal = () => {
		showCustomizeCardModal = false;
	};

	const onApplySettings = async () => {
		await requestChangeDashboardSettings(dashboardId, customCardDashSettings);
		onCloseCustomizeCardModal();
	};
</script>

<Modal
	title={$format('modal_custom_widget_card', {
		default: 'Customize Widget Cards',
	})}
	showModal={showCustomizeCardModal}
	on:close={onCloseCustomizeCardModal}
>
	<svelte:fragment slot="content">
		<Container class="mb-s" padding="0">
			<Text class="mb-s" styleType="h2">
				{$format('label_custom_dash_title', {
					default: 'Card Settings',
				})}
			</Text>
			<Alert
				class="mb-s"
				heading={$format('alert_toggle_custom_widget_cards', {
					default: 'Toggle these items in order to see how your card will look.',
				})}
			/>
			{#each dashCustomizeCheckboxes as checkbox, i}
				<Checkbox
					id={`customize-card-checkbox-${i}`}
					checked={customCardDashSettings[checkbox.type]}
					on:click={(event) => {
						customCardDashSettings[checkbox.type] = event.target?.checked;
					}}
					disabled={customCardDashSettings[checkbox.type] && numberOfActiveCardSettings === 1}
					size="l"
				>
					{checkbox.text}
				</Checkbox>
			{/each}

			<Text class="mt-m mb-s" styleType="h2">
				{$format('text_modal_menu_items_order_preview', {
					default: 'Preview',
				})}
			</Text>

			<PreviewBox minHeight={250}>
				<div class="preview-box-card-wrapper">
					<WidgetCard
						hideAvatar={!customCardDashSettings.havePicture}
						hideTitle={!customCardDashSettings.haveHeading}
						hideSubtitle={!customCardDashSettings.haveHeading}
						hideSlotMain={!customCardDashSettings.haveSmallParams}
						hideSlotBottom={!customCardDashSettings.haveBigParams}
						title="Heading"
						subtitle={{
							value: 'Subheading',
						}}
						avatar={{ image: '/images/room_def/bedroom_img_def_m.jpg' }}
						showFullImage
					>
						<Label
							slot="leftBadge"
							size="s"
							noRadiusType="second"
							icon="fas fa-info-circle"
							value="Badge"
						/>
						<LabelList labelList={previwCardLabelList} slot="main" />
						<svelte:fragment slot="bottom">
							<DeviceButton
								id="devicebutton-iebwnf2"
								aria-label={$format('label_action_button', { default: 'Action button' })}
								icon="fas fa-power-off"
								variant="primary"
								isActive={false}
							/>
						</svelte:fragment>
					</WidgetCard>
				</div>
			</PreviewBox>
		</Container>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{ sm: 'm' }}
			on:click={onCloseCustomizeCardModal}
		>
			{$format('button_cancel')}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{ sm: 'm' }}
			on:click={onApplySettings}
		>
			{$format('button_apply_to_dashboard', { default: 'Apply to Dashboard' })}
		</Button>
	</div>
</Modal>

<style>
	.preview-box-card-wrapper {
		max-width: 160px;
		min-width: 160px;
	}
</style>
